import { DateTime } from 'luxon';
import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import { memberQuitDateLocalDaySchema, type MemberQuitDateSource } from './quit-dates';

// ─── quit date state ────────────────────────────────────────────────────────
//
// What every surface (the member app, the Home card, Atlas, Sona) is told about
// a member's quit date. The phase and the day counts are derived here, from the
// latest `member_quit_dates` row of the member's quit date episode and the
// member's own local day, so the surfaces can never disagree on whether today
// is quit day (ADR-128 Rule 4: derived facts are computed at read time, never
// stored).
//
// A discriminated union rather than one object with nullable counts: a consumer
// that switches on `phase` gets `daysToQuit` as a number in the `pre` branch
// without an assertion, and a `pre` state carrying `daysSinceQuit` cannot be
// built (ADR-117).
//
// `phase` is a closed vocabulary on purpose. Before, on and after a date is the
// whole timeline, so there is no fourth position a later release could add; a
// new dimension (whether the member is abstinent, say) is a new field, which a
// client tolerates, not a new phase, which it would not (ADR-135 Rule 3).
//
// None of the response schemas is `.strict()` (ADR-135 Rule 1).

/** Every phase a quit date state can be in. */
export const QUIT_DATE_PHASES = ['not_applicable', 'none', 'pre', 'day0', 'post'] as const;
export type QuitDatePhase = (typeof QUIT_DATE_PHASES)[number];

const dayCount = z.number().int().min(1);

/**
 * `canChange` is `true` for every member the quit date applies to today. It
 * exists so a later read-only state (an ended episode shown for reference, for
 * example) can ship as a server change: a client that already hides "Change
 * date" on `false` needs no release.
 */
const notApplicableVariant = z.object({ phase: z.literal('not_applicable') });
const noneVariant = z.object({ phase: z.literal('none'), canChange: z.boolean() });
const preVariant = z.object({
  phase: z.literal('pre'),
  quitDate: memberQuitDateLocalDaySchema,
  daysToQuit: dayCount,
  canChange: z.boolean(),
});
const day0Variant = z.object({
  phase: z.literal('day0'),
  quitDate: memberQuitDateLocalDaySchema,
  canChange: z.boolean(),
});
const postVariant = z.object({
  phase: z.literal('post'),
  quitDate: memberQuitDateLocalDaySchema,
  daysSinceQuit: dayCount,
  canChange: z.boolean(),
});

/**
 * The member's quit date state, as the member app reads it.
 *
 * `not_applicable` means the member has no active episode of a quit date
 * programme. It is how a client learns to show nothing without ever being told
 * the member's substance (ADR-015).
 */
export const memberQuitDateStateSchema = z.discriminatedUnion('phase', [
  notApplicableVariant,
  noneVariant,
  preVariant,
  day0Variant,
  postVariant,
]);
export type MemberQuitDateState = z.infer<typeof memberQuitDateStateSchema>;

/** The `GET` and `PUT /members/me/quit-date` response envelope (ADR-002). */
export const memberQuitDateStateResponseSchema = apiEnvelope(memberQuitDateStateSchema);
export type MemberQuitDateStateResponse = z.infer<typeof memberQuitDateStateResponseSchema>;

/** The state of a member the quit date applies to: every phase but `not_applicable`. */
export type ApplicableQuitDateState = Exclude<MemberQuitDateState, { phase: 'not_applicable' }>;

/** A member setting or changing their own quit date. Input, so strict. */
export const memberQuitDateSetRequestSchema = z
  .object({ quitDate: memberQuitDateLocalDaySchema })
  .strict();
export type MemberQuitDateSetRequest = z.infer<typeof memberQuitDateSetRequestSchema>;

// ─── care-team view ─────────────────────────────────────────────────────────

/**
 * Who set the current date, in care-team words. One value per
 * `MEMBER_QUIT_DATE_SOURCE` value, so a staff reader sees "imported" rather
 * than a storage name.
 */
export const QUIT_DATE_SET_BY = ['member', 'care_team', 'system', 'imported'] as const;
export const quitDateSetBySchema = z.enum(QUIT_DATE_SET_BY);
export type QuitDateSetBy = z.infer<typeof quitDateSetBySchema>;

const SET_BY_FOR_SOURCE = {
  mobile_app: 'member',
  atlas_staff: 'care_team',
  system: 'system',
  backend2_import: 'imported',
} as const satisfies Record<MemberQuitDateSource, QuitDateSetBy>;

/** The care-team `setBy` for a stored `source`. */
export function quitDateSetByForSource(source: MemberQuitDateSource): QuitDateSetBy {
  return SET_BY_FOR_SOURCE[source];
}

/**
 * The care-team view for a reader that parses `setBy` with `setBySchema`.
 * `setBy` and `setAt` describe the row that set the current date, so only the
 * variants that carry a date have them. `setAt` is always known; `setBy` is
 * omitted when the stored `source` is one this build cannot name, so a source
 * added by a later deploy never fails the read on the way in.
 */
function careTeamQuitDateViewWith<SetBy extends z.ZodType>(setBySchema: SetBy) {
  const setFields = { setBy: setBySchema.optional(), setAt: z.iso.datetime() };
  return z.discriminatedUnion('phase', [
    notApplicableVariant,
    noneVariant,
    preVariant.extend(setFields),
    day0Variant.extend(setFields),
    postVariant.extend(setFields),
  ]);
}

/** The quit date state as the care team sees it: the member state plus who set the date and when. */
export const careTeamQuitDateViewSchema = careTeamQuitDateViewWith(quitDateSetBySchema);
export type CareTeamQuitDateView = z.infer<typeof careTeamQuitDateViewSchema>;

/** The `GET` and `PUT /internal/members/{ref}/quit-date` response envelope (ADR-002). */
export const careTeamQuitDateViewResponseSchema = apiEnvelope(careTeamQuitDateViewSchema);
export type CareTeamQuitDateViewResponse = z.infer<typeof careTeamQuitDateViewResponseSchema>;

/**
 * What a client holds when the server sends a `setBy` this build does not know.
 * Client-only: the server never produces it.
 */
export const QUIT_DATE_SET_BY_UNRECOGNISED = 'unrecognised' as const;

/**
 * `setBy` as a CLIENT parses it. A new source on the server would otherwise
 * fail the whole care-team view on every open Atlas tab (ADR-135 Rule 3), so an
 * unknown value resolves to the sentinel. `.min(1)` keeps a malformed value
 * (empty, a number, null) failing: the tolerance is for a value this build has
 * not heard of, not for a payload that is not a value at all.
 */
const quitDateSetByReadSchema = z.union([
  quitDateSetBySchema,
  z
    .string()
    .min(1)
    .transform(() => QUIT_DATE_SET_BY_UNRECOGNISED),
]);

/** The care-team view as Atlas parses it, tolerant of a future `setBy` value. */
export const careTeamQuitDateViewReadSchema = careTeamQuitDateViewWith(quitDateSetByReadSchema);
export type CareTeamQuitDateViewRead = z.infer<typeof careTeamQuitDateViewReadSchema>;

/**
 * The `GET` and `PUT /internal/members/{ref}/quit-date` envelope as a CLIENT
 * parses it. The server keeps validating with `careTeamQuitDateViewResponseSchema`;
 * a client reads through this one so a `setBy` added by a later deploy is held as
 * `unrecognised` instead of failing the whole response.
 */
export const careTeamQuitDateViewReadResponseSchema = apiEnvelope(careTeamQuitDateViewReadSchema);
export type CareTeamQuitDateViewReadResponse = z.infer<
  typeof careTeamQuitDateViewReadResponseSchema
>;

// ─── derivation ─────────────────────────────────────────────────────────────

/**
 * A `YYYY-MM-DD` day as a Luxon date at UTC midnight. The day arithmetic below
 * compares two calendar days, so it runs in UTC, where every day is 24 hours
 * long; the member's zone has already decided which day `today` is.
 */
function calendarDay(day: string, label: string): DateTime {
  const parsed = DateTime.fromFormat(day, 'yyyy-MM-dd', { zone: 'utc' });
  if (!parsed.isValid) throw new RangeError(`${label} must be a YYYY-MM-DD calendar day`);
  return parsed;
}

/** The state of a member who has a quit date: `pre`, `day0` or `post`. */
export type DatedQuitDateState = Extract<MemberQuitDateState, { phase: 'pre' | 'day0' | 'post' }>;

/**
 * Whole calendar days from `today` to `quitDate`: positive before the date,
 * zero on it, negative after it. Both must be parsed `YYYY-MM-DD` days;
 * anything else throws, so a malformed day can never become a `NaN` count.
 */
export function quitDateDayOffset(quitDate: string, today: string): number {
  return calendarDay(quitDate, 'quitDate').diff(calendarDay(today, 'today'), 'days').days;
}

/**
 * The phase and day count for a set quit date. `quitDate` and `today` must
 * already be parsed `YYYY-MM-DD` days; anything else throws, so a malformed day
 * can never turn into a `NaN` count. `today` is the member's local day in their
 * own zone, which the quit date resolver decides for every surface.
 */
export function deriveDatedQuitDateState(input: {
  quitDate: string;
  today: string;
  canChange: boolean;
}): DatedQuitDateState {
  const { quitDate, canChange } = input;
  const days = quitDateDayOffset(quitDate, input.today);
  if (days > 0) return { phase: 'pre', quitDate, daysToQuit: days, canChange };
  if (days < 0) return { phase: 'post', quitDate, daysSinceQuit: -days, canChange };
  return { phase: 'day0', quitDate, canChange };
}

/**
 * The quit date state of a member the quit date applies to. `quitDate` is the
 * latest row's date, or `null` when there is no row or the latest row's
 * `quit_date` is null (a row only a system process may write).
 */
export function deriveQuitDateState(input: {
  quitDate: string | null;
  today: string;
  canChange: boolean;
}): ApplicableQuitDateState {
  const { quitDate, today, canChange } = input;
  if (quitDate === null) return { phase: 'none', canChange };
  return deriveDatedQuitDateState({ quitDate, today, canChange });
}

// ─── the new-date rule ──────────────────────────────────────────────────────

/** How far back a newly set quit date may be. */
export const QUIT_DATE_MAX_YEARS_BACK = 5;
/** How far ahead a newly set quit date may be. */
export const QUIT_DATE_MAX_YEARS_AHEAD = 2;

/**
 * The one place both bounds are computed, so the picker limits and the server
 * check cannot drift apart. Calendar years, so 29 February steps to 28 February
 * in both directions.
 */
function newQuitDateBounds(today: string): { earliest: DateTime; latest: DateTime } {
  const day = calendarDay(today, 'today');
  return {
    earliest: day.minus({ years: QUIT_DATE_MAX_YEARS_BACK }),
    latest: day.plus({ years: QUIT_DATE_MAX_YEARS_AHEAD }),
  };
}

function isoDay(day: DateTime): string {
  const iso = day.toISODate();
  if (iso === null) throw new RangeError('today must be a YYYY-MM-DD calendar day');
  return iso;
}

/** The earliest quit date a member or the care team may set, given the member's `today`. */
export function earliestAllowedQuitDate(today: string): string {
  return isoDay(newQuitDateBounds(today).earliest);
}

/** The latest quit date a member or the care team may set, given the member's `today`. */
export function latestAllowedQuitDate(today: string): string {
  return isoDay(newQuitDateBounds(today).latest);
}

/**
 * May this date be set now? Only new writes are held to it: an imported date
 * outside the bounds is still read and shown as it is, and saving the date a
 * member already has is a no-op the writer checks first, so re-saving such a
 * date never fails. A `quitDate` that is not a real day is `false` (it is the
 * value under test); `today` must already be a parsed `YYYY-MM-DD` day, as for
 * `deriveQuitDateState`, and anything else throws.
 */
export function isAllowedNewQuitDate(quitDate: string, today: string): boolean {
  if (!memberQuitDateLocalDaySchema.safeParse(quitDate).success) return false;
  const { earliest, latest } = newQuitDateBounds(today);
  const candidate = calendarDay(quitDate, 'quitDate').toMillis();
  return candidate >= earliest.toMillis() && candidate <= latest.toMillis();
}
